import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import ServiceModal from './ServiceModal'
import FiltersModal, { FilterType } from '../common/FiltersModal'
import LoadingSpinner from '../common/LoadingSpinner'
import Breadcrumb from '../common/Breadcrumb'
import ConfirmationModal from '../common/ConfirmationModal'
import EmptyPageResults from '../common/EmptyPageResults'
import SimpleButton from '../common/SimpleButton'
import DashboardHeader from '../common/DashboardHeader/DashboardHeader'
import GenericTable from '../common/GenericTable'
import ActionsMenu from '../common/ActionsMenu'
import { toast } from '../common/Toast'
import Dropdown from '../common/Dropdown'
import SearchWithFilter from '../common/SearchWithFilter'
import { useWindowSize } from '../../libs/hooks'
import { useDeepCompareEffect } from '../../hooks/useDeepCompareEffect'
import { loadAllPoliciesOptions, loadPolicyTypes } from '../../actions/policies'
import { delService, changeServiceStatus, loadServices, clearServicesMessages } from '../../actions/services'
import { DisabledIcon, EnabledIcon, MenuKebabVerticalIcon, MenuKebabVerticalBackgroundIcon } from '../../icons'
import { servicesDeletePermission, servicesWritePermission } from '../../utils/permissionValidation'
import './services.scss'

const orderingFields = [
  {
    field: 'name',
    order: 'asc',
    translationKey: 'name_asc',
  },
  {
    field: 'name',
    order: 'desc',
    translationKey: 'name_desc',
  },
  {
    field: 'policy',
    order: 'asc',
    translationKey: 'policy_asc',
  },
  {
    field: 'policy',
    order: 'desc',
    translationKey: 'policy_desc',
  },
  {
    field: 'status',
    order: 'asc',
    translationKey: 'status_asc',
  },
  {
    field: 'status',
    order: 'desc',
    translationKey: 'status_desc',
  },
]

const Services = (props) => {
  const { t } = useTranslation()
  const isMobile = useWindowSize()[0] <= 768
  const [servicesTableData, setServicesTableData] = useState([])
  const [filteredPolicies, setFilteredPolicies] = useState([])
  const [filterQuery, setFilterQuery] = useState(null)
  const [selectedFilterPolicies, setSelectedFilterPolicies] = useState([])
  const [selectedFilterStatuses, setSelectedFilterStatuses] = useState([])
  const [orderBy, setOrderBy] = useState(null)
  const [shouldApplyFilters, setShouldApplyFilters] = useState(true)
  const [selectedPage, setSelectedPage] = useState(1)
  const [canLoadMore, setCanLoadMore] = useState(false)
  const [isFirstLoad, setIsFirstLoad] = useState(true)
  const [expandedService, setExpandedService] = useState(0)
  const [editService, setEditService] = useState(null)
  const [serviceIdToDelete, setServiceIdToDelete] = useState(null)
  const [showSpinner, setShowSpinner] = useState(false)
  const [showFiltersModal, setShowFiltersModal] = useState(false)
  const [showServiceModal, setShowServiceModal] = useState(false)
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false)
  const [showActionsMenu, setShowActionsMenu] = useState(null)

  useEffect(() => {
    if (props.policies.length === 0) {
      props.actions.loadAllPoliciesOptions()
    }

    if (props.policyTypes.length === 0) {
      props.actions.loadPolicyTypes()
    }
  }, [props.actions])

  useEffect(() => {
    if (props.policies.length > 0 && props.policyTypes.length > 0) {
      const servicesPolicyTypeId = props.policyTypes.find((policyType) => policyType.name === 'Services')?.id

      const servicesPolicies = []

      props.policies.forEach((policy) => {
        if (policy.policyTypeId === servicesPolicyTypeId) {
          servicesPolicies.push(policy)
        }
      })

      setFilteredPolicies(servicesPolicies)
    }
  }, [props.policies, props.policyTypes])

  useEffect(() => {
    if (props.services.length > 0 && filteredPolicies.length > 0) {
      const servicesWithPolicyName = []

      props.services.forEach((service) => {
        const policyName = filteredPolicies.find((policy) => policy.id === service.policyId)?.name || ''

        servicesWithPolicyName.push({
          ...service,
          policyName: policyName,
        })
      })

      const tableData = servicesWithPolicyName.map((service, index) => {
        return {
          index,
          identifier: service.id,
          name: service.name,
          description: service.description,
          policyName: service.policyName,
          status: service.isEnabled,
          actions: [
            {
              label: 'edit',
              onActionClick: (e) => {
                e.stopPropagation()
                handleEditService(service.id)
              },
            },
            {
              label: service.isEnabled ? 'deactivate' : 'activate',
              onActionClick: (e) => {
                e.stopPropagation()
                handleChangeStatusService(service.id, service.isEnabled)
              },
              requiredPermission: servicesWritePermission,
            },
            {
              label: 'delete',
              onActionClick: (e) => {
                e.stopPropagation()
                showConfirmationModal(service.id)
              },
              isDisabled: service.isEnabled,
              requiredPermission: servicesDeletePermission,
            },
          ],
        }
      })

      setServicesTableData(tableData)
    } else {
      setServicesTableData([])
    }
  }, [props.services, filteredPolicies])

  useEffect(() => {
    if (props.errorMessage) {
      toast.error(props.errorMessage)

      props.actions.clearServicesMessages()
    }
  }, [props.errorMessage])

  useEffect(() => {
    if (props.successMessage) {
      toast.success(props.successMessage)

      props.actions.clearServicesMessages()
    }
  }, [props.successMessage])

  useEffect(() => {
    setCanLoadMore(props.canLoadMore)
  }, [props.canLoadMore])

  useEffect(() => {
    setShowSpinner(props.isLoading)
  }, [props.isLoading])

  useEffect(() => {
    if (selectedPage && selectedPage > 1) {
      handleLoadServices(false)
    }
  }, [selectedPage])

  useDeepCompareEffect(() => {
    let changeValueTimeout = window.setTimeout(
      () => {
        if (shouldApplyFilters) {
          setSelectedPage(1)
          handleLoadServices(true)
        }

        setShouldApplyFilters(true)
      },
      filterQuery ? 1000 : 0,
    )

    return () => {
      clearTimeout(changeValueTimeout)
    }
  }, [filterQuery, selectedFilterPolicies, selectedFilterStatuses, orderBy])

  const handleLoadServices = (withReset) => {
    let queryParams = {}

    if (filterQuery) {
      queryParams = { ...queryParams, query: filterQuery }
    }

    if (selectedFilterPolicies.length > 0) {
      const selectedPolicyIds = filteredPolicies.filter((policy) => selectedFilterPolicies.includes(policy.name)).map((policy) => policy.id)

      queryParams = { ...queryParams, policy: selectedPolicyIds }
    }

    if (selectedFilterStatuses.length === 1) {
      const isActiveStatus = true

      queryParams = { ...queryParams, isEnabled: selectedFilterStatuses.indexOf('inactive') >= 0 ? false : isActiveStatus }
    }

    if (orderBy?.field && orderBy?.order) {
      queryParams = { ...queryParams, orderBy: orderBy.field, order: orderBy.order }
    }

    queryParams = {
      ...queryParams,
      startPage: withReset ? 1 : selectedPage,
    }

    props.actions.loadServices(queryParams, withReset).then(() => setIsFirstLoad(false))
  }

  const handleSearch = (value) => {
    if (value?.length > 2) {
      setShowSpinner(true)
      setFilterQuery(value)
    } else if (value?.length === 0 && filterQuery?.length > 0) {
      setShowSpinner(true)
      setFilterQuery(null)
    }
  }

  const handleLoadMore = () => {
    setSelectedPage((prevValue) => (prevValue ? prevValue + 1 : 2))
  }

  const handleSort = (orderingField) => {
    setOrderBy(orderingFields.find((o) => o.field === orderingField.field && o.order === orderingField.order))
  }

  const handleChangeStatusService = (id, isEnabled) => {
    props.actions.changeServiceStatus(id, isEnabled ? 'disable' : 'enable', true)
  }

  const showConfirmationModal = (id) => {
    setServiceIdToDelete(id)
    setShowDeleteConfirmationModal(true)
  }

  const handleDeleteService = (id) => {
    setShowDeleteConfirmationModal(false)
    props.actions.delService(id)
  }

  const handleEditService = (serviceId) => {
    let service = props.services.find((service) => service.id === serviceId)
    const policyName = filteredPolicies.find((policy) => policy.id === service.policyId)?.name || ''

    service = {
      ...service,
      policyName: policyName,
    }

    if (service) {
      setEditService(service)
      setShowServiceModal(true)
    }
  }

  const handleCloseModal = () => {
    setShowServiceModal((prevVal) => !prevVal)
    setEditService(null)
  }

  const handleClearFilters = () => {
    setSelectedFilterPolicies([])
    setSelectedFilterStatuses([])
  }

  const selectOrderByOptions = (orderByTranslationKey) => {
    const sortOption = orderingFields.find((field) => field.translationKey === orderByTranslationKey)
    handleSort(sortOption)
  }

  const toggleShowActionsMenu = (index) => {
    setShowActionsMenu(showActionsMenu === index ? null : index)
  }

  return (
    <div className="services-wrapper">
      <Breadcrumb path={window.location.pathname} />
      <DashboardHeader headerText={t('nav.services')}>
        <SimpleButton
          className="submit-button"
          onClick={() => {
            setEditService(null)
            setShowServiceModal((prevVal) => !prevVal)
          }}
        >
          {t('services_context.add_new_service')}
        </SimpleButton>
      </DashboardHeader>
      <div className="separator" />
      <div className="actions-wrapper">
        <SearchWithFilter
          onSearch={handleSearch}
          showFiltersModal={() => setShowFiltersModal((prevVal) => !prevVal)}
          onResetFilters={handleClearFilters}
          showCounterBadge={selectedFilterStatuses.length > 0 || selectedFilterPolicies.length > 0}
          counterBadge={selectedFilterStatuses.length + selectedFilterPolicies.length}
        />
        {isMobile && (
          <div className="order-by-mobile-wrapper">
            <Dropdown
              className="dropdown-input"
              placeholder={t('select_ordering')}
              options={orderingFields.map((field) => ({ key: field.translationKey, value: t(`sortable_context.${field.translationKey}`) }))}
              initialSelectedKey={orderBy?.translationKey}
              onChange={(orderBy) => selectOrderByOptions(orderBy.key)}
            />
          </div>
        )}
      </div>
      {!isMobile && servicesTableData.length > 0 && (
        <GenericTable
          className="services-table"
          data={servicesTableData}
          headers={[
            {
              title: t('name'),
              orderKey: 'name',
            },
            {
              title: t('description'),
            },
            {
              title: t('policy'),
              orderKey: 'policy',
            },
            {
              title: t('status'),
              orderKey: 'status',
            },
            {
              title: '',
            },
          ]}
          keys={['name', 'description', 'policyName', 'status', 'action']}
          keyRenderer={{
            status: (item) => {
              return (
                <div className="status-wrapper">
                  {item.status ? (
                    <div className="status-active">
                      <EnabledIcon />
                      <p>{t('active')}</p>
                    </div>
                  ) : (
                    <div className="status-inactive">
                      <DisabledIcon />
                      <p>{t('inactive')}</p>
                    </div>
                  )}
                </div>
              )
            },
            action: (item) => {
              return (
                <div
                  className="table-actions-wrapper"
                  onClick={(e) => {
                    e.stopPropagation()
                    toggleShowActionsMenu(item['index'])
                  }}
                >
                  {showActionsMenu === item['index'] ? <MenuKebabVerticalBackgroundIcon /> : <MenuKebabVerticalIcon />}
                  {showActionsMenu === item['index'] && (
                    <ActionsMenu setHideMenu={() => setShowActionsMenu(null)} actions={item.actions} isLoading={showSpinner} />
                  )}
                </div>
              )
            },
          }}
          onRowClick={(service) => handleEditService(service.identifier)}
          activeSort={orderBy}
          onSort={(orderingField) => handleSort(orderingField)}
          isLoading={showSpinner}
        />
      )}
      {isMobile &&
        servicesTableData.map((service, index) => (
          <div key={index} className={'card' + (index === 0 ? ' with-margin-top' : '')} onClick={() => setExpandedService(index)}>
            <div className={'card-item' + (expandedService !== index ? ' align-center' : '')}>
              <div className="card-item-title">{t('name')}</div>
              <div className={'card-item-body' + (expandedService !== index ? ' align-center' : '')}>
                <div>{service.name}</div>
                <div
                  className="card-actions"
                  onClick={(e) => {
                    e.stopPropagation()
                    setShowActionsMenu(index)
                  }}
                >
                  {showActionsMenu === index ? <MenuKebabVerticalBackgroundIcon /> : <MenuKebabVerticalIcon />}
                  {showActionsMenu === index && <ActionsMenu setHideMenu={() => setShowActionsMenu(null)} actions={service.actions} isLoading={showSpinner} />}
                </div>
              </div>
            </div>
            {expandedService === index && (
              <>
                <div className="card-item">
                  <div className="card-item-title">{t('description')}</div>
                  <div className="card-item-body">{service.description}</div>
                </div>
                <div className="card-item">
                  <div className="card-item-title">{t('policy')}</div>
                  <div className="card-item-body">{service.policyName}</div>
                </div>
                <div className="card-item">
                  <div className="card-item-title">{t('status')}</div>
                  <div className="card-item-body">
                    <div className="card-item-body-status">
                      {service.status ? (
                        <div className="status-active">
                          <EnabledIcon />
                          <p>{t('active')}</p>
                        </div>
                      ) : (
                        <div className="status-inactive">
                          <DisabledIcon />
                          <p>{t('inactive')}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        ))}
      {!showSpinner && !isFirstLoad && servicesTableData.length === 0 && (
        <EmptyPageResults title={t('services_context.no_services')} subtitle={t('services_context.no_services_tips')} />
      )}
      {canLoadMore && (
        <div className="buttons-wrapper">
          <SimpleButton className="load-more-button" onClick={() => handleLoadMore()}>
            {t('load_more')}
          </SimpleButton>
        </div>
      )}
      {showSpinner && (
        <div className={'spinner-wrapper'}>
          <LoadingSpinner />
        </div>
      )}
      {showServiceModal && <ServiceModal onToggle={handleCloseModal} service={editService} policies={filteredPolicies} />}
      {showFiltersModal && (
        <FiltersModal
          filters={[
            {
              name: t('nav.policies'),
              type: FilterType.multiSelect,
              dataset: filteredPolicies.map((policy) => policy.name),
              input: selectedFilterPolicies,
              output: (filterBusinessRulesPolicies) => {
                setSelectedFilterPolicies(filterBusinessRulesPolicies)
              },
            },
            {
              name: t('status'),
              type: FilterType.multiSelect,
              dataset: ['active', 'inactive'],
              input: selectedFilterStatuses,
              output: (filterBUsinessRulesStatuses) => {
                setSelectedFilterStatuses(filterBUsinessRulesStatuses)
              },
            },
          ]}
          shouldFilter={(shouldApply) => setShouldApplyFilters(shouldApply)}
          closeFilters={() => setShowFiltersModal(false)}
          resetFilters={() => {
            setSelectedFilterPolicies([])
            setSelectedFilterStatuses([])
          }}
        />
      )}
      {showDeleteConfirmationModal && (
        <ConfirmationModal
          onToggle={() => setShowDeleteConfirmationModal(false)}
          message={t('services_context.delete_confirmation')}
          acceptButtonText={t('delete')}
          onCancel={() => setShowDeleteConfirmationModal(false)}
          onAccept={() => handleDeleteService(serviceIdToDelete)}
          isWarning
          requiredPermission={servicesDeletePermission}
        />
      )}
    </div>
  )
}

function stateToProps({ services, policies }) {
  return {
    services: services?.services || [],
    policies: policies?.allPoliciesOptions || [],
    policyTypes: policies?.policyTypes || [],
    isLoading: services?.servicesLoading || services?.serviceLoading || policies?.policiesLoading,
    canLoadMore: services?.canLoadMore,
    errorMessage: services?.errorMessage,
    successMessage: services?.successMessage,
  }
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadServices,
        loadAllPoliciesOptions,
        loadPolicyTypes,
        delService,
        changeServiceStatus,
        clearServicesMessages,
      },
      dispatch,
    ),
  }
}

export default connect(stateToProps, dispatchToProps)(Services)
